import { useEffect, useMemo, useRef, useState } from 'react';

const NAVY = '#243671';
const CYAN = '#189bd7';
const CHARCOAL = '#262626';

interface Node {
  x: number;
  y: number;
  r: number;
  type: 'star' | 'center' | 'scatter';
  delay: number;
  glow: boolean;
  alpha?: number;
}

interface Edge {
  from: number;
  to: number;
  type: 'star' | 'toCenter' | 'scatter';
  delay: number;
  opacity?: number;
}

interface Zone {
  x: number;
  y: number;
  spread: number;
}

interface Particle {
  x: number;
  y: number;
  r: number;
  duration: number;
  delay: number;
  opacity: number;
}

function seededRandom(seed: number) {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

function generateConstellation(width: number, height: number): { nodes: Node[]; edges: Edge[] } {
  const rng = seededRandom(42);
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  const cx = width * 0.48;
  const cy = height * 0.38;

  const starRadius = Math.min(width, height) * 0.16;
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i - Math.PI / 2;
    nodes.push({
      x: cx + Math.cos(angle) * starRadius,
      y: cy + Math.sin(angle) * starRadius,
      r: 3,
      type: 'star',
      delay: i * 0.15,
      glow: i === 4,
    });
  }
  nodes.push({ x: cx, y: cy, r: 4.5, type: 'center', delay: 0, glow: false });

  for (let i = 0; i < 6; i++) {
    edges.push({ from: 6, to: i, type: 'star', delay: i * 0.15 });
  }

  const zones: Zone[] = [
    { x: 0.12, y: 0.1, spread: 0.08 },
    { x: 0.85, y: 0.15, spread: 0.06 },
    { x: 0.15, y: 0.28, spread: 0.07 },
    { x: 0.78, y: 0.32, spread: 0.05 },
    { x: 0.25, y: 0.55, spread: 0.1 },
    { x: 0.7, y: 0.52, spread: 0.08 },
    { x: 0.1, y: 0.68, spread: 0.06 },
    { x: 0.88, y: 0.65, spread: 0.05 },
    { x: 0.5, y: 0.72, spread: 0.12 },
    { x: 0.3, y: 0.82, spread: 0.08 },
    { x: 0.72, y: 0.85, spread: 0.06 },
    { x: 0.15, y: 0.92, spread: 0.05 },
    { x: 0.6, y: 0.92, spread: 0.07 },
    { x: 0.4, y: 0.18, spread: 0.06 },
    { x: 0.9, y: 0.48, spread: 0.04 },
    { x: 0.05, y: 0.48, spread: 0.04 },
  ];

  const scattered: Node[] = [];
  zones.forEach((zone, i) => {
    const distFromCenter = Math.sqrt(Math.pow(zone.x - 0.48, 2) + Math.pow(zone.y - 0.38, 2));
    const distFactor = Math.min(distFromCenter / 0.5, 1);
    const baseR = 1.2 + (1 - distFactor) * 2.5;
    const baseAlpha = 0.25 + (1 - distFactor) * 0.55;

    scattered.push({
      x: (zone.x + (rng() - 0.5) * zone.spread) * width,
      y: (zone.y + (rng() - 0.5) * zone.spread) * height,
      r: baseR + rng() * 0.5,
      type: 'scatter',
      delay: 0.8 + i * 0.08,
      glow: rng() > 0.9,
      alpha: baseAlpha,
    });
  });

  const scatterStart = nodes.length;
  nodes.push(...scattered);

  const centerIdx = 6;
  const threshold = Math.min(width, height) * 0.25;
  const centerThreshold = Math.min(width, height) * 0.6;

  for (let i = scatterStart; i < nodes.length; i++) {
    const dxC = nodes[i].x - nodes[centerIdx].x;
    const dyC = nodes[i].y - nodes[centerIdx].y;
    const distToCenter = Math.sqrt(dxC * dxC + dyC * dyC);
    if (distToCenter < centerThreshold) {
      edges.push({
        from: i,
        to: centerIdx,
        type: 'toCenter',
        delay: nodes[i].delay,
        opacity: Math.max(0.08, 0.3 - (distToCenter / centerThreshold) * 0.25),
      });
    }

    let closest = -1;
    let closestDist = Infinity;
    for (let j = 0; j < i; j++) {
      if (j === centerIdx) continue;
      const dx = nodes[i].x - nodes[j].x;
      const dy = nodes[i].y - nodes[j].y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < threshold && dist < closestDist) {
        closestDist = dist;
        closest = j;
      }
    }
    if (closest >= 0) {
      edges.push({ from: i, to: closest, type: 'scatter', delay: nodes[i].delay });
    }
    if (rng() > 0.7) {
      let second = -1;
      let secondDist = Infinity;
      for (let j = 0; j < i; j++) {
        if (j === closest || j === centerIdx) continue;
        const dx = nodes[i].x - nodes[j].x;
        const dy = nodes[i].y - nodes[j].y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < threshold * 1.2 && dist < secondDist) {
          secondDist = dist;
          second = j;
        }
      }
      if (second >= 0) {
        edges.push({ from: i, to: second, type: 'scatter', delay: nodes[i].delay + 0.1 });
      }
    }
  }

  return { nodes, edges };
}

function ParticlesLayer({ width, height }: { width: number; height: number }) {
  const particles = useMemo<Particle[]>(() => {
    const rng = seededRandom(99);
    return Array.from({ length: 30 }, () => ({
      x: rng() * width,
      y: rng() * height,
      r: 0.5 + rng() * 1,
      duration: 15 + rng() * 25,
      delay: rng() * -20,
      opacity: 0.1 + rng() * 0.2,
    }));
  }, [width, height]);

  return (
    <>
      {particles.map((p, i) => (
        <circle
          key={i}
          cx={p.x}
          cy={p.y}
          r={p.r}
          fill="white"
          opacity={p.opacity}
          style={{
            animation: `float-particle-${i % 5} ${p.duration}s ease-in-out ${p.delay}s infinite`,
          }}
        />
      ))}
    </>
  );
}

function LogoMark({ x, y, size }: { x: number; y: number; size: number }) {
  const hubX = 11.7;
  const hubY = 13.0;
  const scale = size / 13.5;
  const offsetX = x - hubX * scale;
  const offsetY = y - hubY * scale;

  return (
    <g transform={`translate(${offsetX}, ${offsetY}) scale(${scale})`} opacity={0.15}>
      <path
        d="M13.3252 15.9768C13.2552 17.0168 13.2952 17.9768 13.2952 19.0068C13.2952 19.6768 13.5952 20.2967 14.0252 20.7867C15.1552 22.0767 15.0452 23.9767 13.8752 25.1467C12.7052 26.3167 10.7052 26.3768 9.4652 25.2068C8.2252 24.0368 8.11521 22.1067 9.25521 20.7867C10.2552 19.6267 10.2252 17.6167 9.9452 16.1967C9.4252 13.5667 7.4252 11.5168 4.7952 11.0068C4.2352 10.8968 3.6852 11.0467 3.1152 11.0367C1.4552 11.0067 0.205205 9.70675 0.0752053 8.17675C-0.0647947 6.55675 1.0452 5.12675 2.5652 4.80675C4.1752 4.47675 5.7352 5.37675 6.2452 6.94675C6.4452 7.56675 6.7952 8.12675 7.3652 8.46675L9.9752 10.0468L10.0052 7.15675C10.0052 6.31675 9.6452 5.66675 9.1552 5.05675C8.0952 3.74675 8.3352 1.87675 9.5352 0.79675C10.7352 -0.28325 12.6752 -0.28325 13.8752 0.90675C15.0752 2.09675 15.1452 3.94675 14.0252 5.23675C13.5852 5.74675 13.3052 6.35675 13.3152 7.04675C13.3352 8.03675 13.2752 8.97675 13.3452 10.0367L15.8152 8.54675C16.4252 8.17675 16.8452 7.63675 17.0552 6.95675C17.5652 5.29675 19.2752 4.39675 20.8852 4.81675C22.4952 5.23675 23.5352 6.90675 23.1752 8.56675C22.8152 10.2268 21.2152 11.2968 19.5052 10.9968C18.7852 10.8668 18.0952 11.0168 17.4852 11.3968L14.8652 13.0068L17.5852 14.6768C18.2752 15.0968 18.9852 15.0968 19.7652 14.9968C21.4652 14.7668 22.9752 15.9567 23.2552 17.6067C23.5352 19.2567 22.5052 20.8167 20.8652 21.2367C19.2252 21.6567 17.5852 20.7468 17.0752 19.0968C16.8752 18.4468 16.5052 17.8867 15.9152 17.5367L13.3552 15.9868L13.3252 15.9768Z"
        fill="white"
      />
      <path
        d="M2.45521 15.0361C4.24521 14.6061 5.92521 15.7161 6.31521 17.4261C6.70521 19.1361 5.65521 20.8761 3.84521 21.2561C2.24521 21.5961 0.545211 20.6161 0.105211 18.9161C-0.334789 17.2161 0.63521 15.4661 2.45521 15.0361Z"
        fill="#179BD7"
      />
    </g>
  );
}

const ULBrandBanner = () => {
  const [dimensions, setDimensions] = useState({ width: 420, height: 800 });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({ width: rect.width, height: rect.height });
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const { width, height } = dimensions;
  const { nodes, edges } = useMemo(() => generateConstellation(width, height), [width, height]);

  return (
    <div
      ref={containerRef}
      className="fixed top-0 right-0 hidden h-screen w-1/3 overflow-hidden lg:flex"
      style={{
        background: `linear-gradient(165deg, ${NAVY} 0%, #1a2a5a 40%, #152248 70%, ${CHARCOAL} 100%)`,
      }}
    >
      <style>{`
        @keyframes fadeInNode {
          0% { opacity: 0; transform: scale(0); }
          60% { opacity: var(--target-opacity, 0.8); transform: scale(1.2); }
          100% { opacity: var(--target-opacity, 0.8); transform: scale(1); }
        }
        @keyframes fadeInEdge {
          0% { opacity: 0; stroke-dashoffset: 100; }
          100% { opacity: var(--target-opacity, 0.15); stroke-dashoffset: 0; }
        }
        @keyframes pulseGlow {
          0%, 100% { opacity: 0.4; r: 6; }
          50% { opacity: 0.8; r: 10; }
        }
        @keyframes fadeInText {
          0% { opacity: 0; transform: translateY(12px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes float-particle-0 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(8px,-12px)} }
        @keyframes float-particle-1 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(-6px,10px)} }
        @keyframes float-particle-2 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(12px,6px)} }
        @keyframes float-particle-3 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(-10px,-8px)} }
        @keyframes float-particle-4 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(5px,14px)} }
      `}</style>

      {/* Subtle grid overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
          zIndex: 1,
        }}
      />

      {/* Radial glow behind center cluster */}
      <div
        style={{
          position: 'absolute',
          left: '38%',
          top: '28%',
          width: '40%',
          height: '25%',
          background: `radial-gradient(ellipse, ${CYAN}18 0%, transparent 70%)`,
          filter: 'blur(40px)',
          zIndex: 1,
        }}
      />

      {/* SVG Constellation */}
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={{ position: 'absolute', inset: 0, zIndex: 2 }}
      >
        <defs>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id="edgeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={CYAN} stopOpacity="0.3" />
            <stop offset="100%" stopColor="white" stopOpacity="0.08" />
          </linearGradient>
        </defs>

        <ParticlesLayer width={width} height={height} />

        <LogoMark x={width * 0.48} y={height * 0.38} size={Math.min(width, height) * 0.22} />

        {edges.map((edge, i) => {
          const from = nodes[edge.from];
          const to = nodes[edge.to];
          const isStar = edge.type === 'star';
          const isToCenter = edge.type === 'toCenter';
          const baseOpacity = edge.opacity || (isStar ? 0.25 : 0.12);
          return (
            <line
              key={`edge-${i}`}
              x1={from.x}
              y1={from.y}
              x2={to.x}
              y2={to.y}
              stroke={isStar || isToCenter ? CYAN : 'white'}
              strokeWidth={isStar ? 0.8 : 0.4}
              opacity={0}
              strokeDasharray={isStar ? 'none' : isToCenter ? '6 6' : '4 4'}
              style={{
                animation: `fadeInEdge 0.8s ease ${edge.delay + 0.3}s forwards`,
                ['--target-opacity' as string]: baseOpacity,
              }}
            />
          );
        })}

        {nodes.map((node, i) => (
          <g key={`node-${i}`}>
            {(node.glow || node.type === 'center') && (
              <circle
                cx={node.x}
                cy={node.y}
                r={node.r * 2.5}
                fill="none"
                stroke={CYAN}
                strokeWidth={0.5}
                opacity={0}
                style={{
                  animation: `pulseGlow 3s ease-in-out ${node.delay + 1}s infinite`,
                }}
              />
            )}
            <circle
              cx={node.x}
              cy={node.y}
              r={node.r}
              fill={node.glow ? CYAN : 'white'}
              opacity={0}
              filter={node.glow || node.type === 'center' ? 'url(#glow)' : undefined}
              style={{
                animation: `fadeInNode 0.5s ease ${node.delay}s forwards`,
                transformOrigin: `${node.x}px ${node.y}px`,
                ['--target-opacity' as string]:
                  node.alpha || (node.type === 'star' ? 0.8 : node.type === 'center' ? 1 : 0.5),
              }}
            />
          </g>
        ))}
      </svg>

      {/* Content overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 3,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          padding: '40px 32px',
        }}
      >
        {/* Dotstar company logo — top right */}
        <div
          style={{
            position: 'absolute',
            top: 32,
            right: 32,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            animation: 'fadeInText 0.8s ease 0.2s both',
          }}
        >
          <svg width="140" height="33" viewBox="0 0 171 40" fill="none">
            <path
              d="M19.9448 23.9257C19.8448 25.4757 19.8948 26.9157 19.9048 28.4557C19.9048 29.4657 20.3448 30.3857 20.9948 31.1257C22.6848 33.0657 22.5248 35.9057 20.7648 37.6557C18.9448 39.4557 16.0248 39.4957 14.1548 37.7457C12.2848 35.9957 12.1248 33.1157 13.8348 31.1257C15.3348 29.3857 15.2948 26.3857 14.8748 24.2557C14.0948 20.3157 11.1048 17.2457 7.1648 16.4857C6.3248 16.3257 5.5048 16.5457 4.6448 16.5257C2.1648 16.4857 0.294805 14.5457 0.0848046 12.2357C-0.135195 9.80571 1.5348 7.66571 3.8148 7.19571C6.2248 6.69571 8.5648 8.04571 9.3248 10.4057C9.6248 11.3457 10.1548 12.1657 10.9948 12.6757L14.8948 15.0357L14.9348 10.7157C14.9448 9.4557 14.3948 8.48571 13.6548 7.56571C12.0748 5.60571 12.4248 2.81571 14.2248 1.19571C16.0248 -0.424294 18.9248 -0.424294 20.7248 1.35571C22.4648 3.07571 22.6348 5.90571 20.9548 7.83571C20.2948 8.59571 19.8748 9.50571 19.8848 10.5457C19.9048 12.0357 19.8248 13.4257 19.9348 15.0157L23.6348 12.7857C24.5448 12.2357 25.1748 11.4257 25.4948 10.4057C26.2548 7.92571 28.8248 6.5757 31.2248 7.2057C33.6248 7.8357 35.1948 10.3357 34.6448 12.8157C34.0948 15.2957 31.7148 16.8957 29.1548 16.4457C28.0748 16.2557 27.0448 16.4757 26.1348 17.0357L22.2148 19.4557L26.2848 21.9657C27.3148 22.5957 28.3748 22.5957 29.5448 22.4457C32.0948 22.1057 34.3448 23.8757 34.7648 26.3457C35.1848 28.7557 33.6448 31.1557 31.1848 31.7857C28.7248 32.4157 26.2748 31.0457 25.5148 28.5857C25.2148 27.6157 24.6648 26.7757 23.7848 26.2457L19.9548 23.9257H19.9448Z"
              fill="white"
            />
            <path
              d="M3.6748 22.5263C6.3548 21.8863 8.8648 23.5463 9.4548 26.1063C10.0448 28.6663 8.46481 31.2663 5.75481 31.8463C3.35481 32.3563 0.804808 30.8863 0.154808 28.3363C-0.495192 25.7863 0.944802 23.1663 3.6648 22.5163L3.6748 22.5263Z"
              fill="#179BD7"
            />
            <path
              d="M41.5248 17.4059C42.3348 15.8959 43.4448 14.7259 44.8548 13.8859C46.2548 13.0459 47.8248 12.6259 49.5448 12.6259C50.8148 12.6259 52.0748 12.9059 53.3148 13.4559C54.5548 14.0059 55.5348 14.7459 56.2648 15.6659V6.25586H60.3348V32.4459H56.2648V29.5059C55.6048 30.4459 54.6948 31.2259 53.5248 31.8459C52.3548 32.4559 51.0148 32.7659 49.5048 32.7659C47.8048 32.7659 46.2548 32.3359 44.8548 31.4759C43.4548 30.6159 42.3448 29.4159 41.5248 27.8659C40.7148 26.3259 40.3048 24.5659 40.3048 22.6159C40.3048 20.6659 40.7148 18.9259 41.5248 17.4159V17.4059ZM55.4348 19.1759C54.8848 18.1859 54.1548 17.4259 53.2548 16.9159C52.3548 16.3959 51.3948 16.1359 50.3548 16.1359C49.3148 16.1359 48.3448 16.3859 47.4548 16.8959C46.5548 17.4059 45.8348 18.1459 45.2748 19.1259C44.7248 20.1059 44.4448 21.2659 44.4448 22.6159C44.4448 23.9659 44.7248 25.1359 45.2748 26.1559C45.8248 27.1659 46.5648 27.9459 47.4648 28.4759C48.3648 29.0059 49.3348 29.2759 50.3448 29.2759C51.3548 29.2759 52.3448 29.0159 53.2448 28.4959C54.1448 27.9759 54.8648 27.2159 55.4248 26.2159C55.9748 25.2159 56.2548 24.0359 56.2548 22.6959C56.2548 21.3559 55.9748 20.1859 55.4248 19.1959L55.4348 19.1759Z"
              fill="#179BD7"
            />
            <path
              d="M69.1148 31.505C67.6248 30.665 66.4648 29.485 65.6148 27.965C64.7648 26.445 64.3448 24.685 64.3448 22.675C64.3448 20.665 64.7848 18.935 65.6548 17.405C66.5248 15.875 67.7148 14.695 69.2248 13.865C70.7348 13.035 72.4248 12.625 74.2848 12.625C76.1448 12.625 77.8348 13.035 79.3448 13.865C80.8548 14.695 82.0448 15.875 82.9148 17.405C83.7848 18.935 84.2248 20.695 84.2248 22.675C84.2248 24.655 83.7748 26.415 82.8848 27.945C81.9848 29.475 80.7648 30.665 79.2248 31.505C77.6748 32.345 75.9748 32.765 74.1148 32.765C72.2548 32.765 70.6148 32.345 69.1248 31.505H69.1148ZM77.0148 28.515C77.9248 28.015 78.6648 27.275 79.2248 26.285C79.7848 25.295 80.0748 24.095 80.0748 22.675C80.0748 21.255 79.8048 20.065 79.2648 19.085C78.7248 18.105 78.0048 17.365 77.1048 16.875C76.2048 16.375 75.2448 16.135 74.2048 16.135C73.1648 16.135 72.2048 16.385 71.3248 16.875C70.4448 17.375 69.7348 18.105 69.2148 19.085C68.6948 20.065 68.4348 21.265 68.4348 22.675C68.4348 24.775 68.9748 26.395 70.0448 27.545C71.1148 28.685 72.4648 29.265 74.0948 29.265C75.1348 29.265 76.1048 29.015 77.0148 28.525V28.515Z"
              fill="#179BD7"
            />
            <path
              d="M92.9248 16.2357V27.0257C92.9248 27.7557 93.0948 28.2857 93.4348 28.5957C93.7748 28.9157 94.3648 29.0757 95.1848 29.0757H97.6648V32.4357H94.4848C92.6648 32.4357 91.2748 32.0157 90.3048 31.1657C89.3348 30.3157 88.8548 28.9357 88.8548 27.0257V16.2357H86.5548V12.9457H88.8548V8.0957H92.9248V12.9457H97.6648V16.2357H92.9248Z"
              fill="#179BD7"
            />
            <path
              d="M105.575 31.036C104.305 30.456 103.295 29.676 102.545 28.686C101.795 27.696 101.395 26.596 101.325 25.396H106.315C106.405 26.156 106.785 26.776 107.435 27.276C108.085 27.776 108.895 28.016 109.865 28.016C110.835 28.016 111.545 27.826 112.075 27.446C112.605 27.066 112.875 26.586 112.875 25.996C112.875 25.356 112.545 24.876 111.905 24.566C111.255 24.246 110.225 23.896 108.805 23.526C107.345 23.176 106.145 22.806 105.215 22.426C104.285 22.046 103.475 21.466 102.805 20.686C102.135 19.906 101.795 18.856 101.795 17.536C101.795 16.446 102.105 15.456 102.735 14.566C103.365 13.666 104.255 12.956 105.425 12.446C106.595 11.926 107.965 11.666 109.555 11.666C111.895 11.666 113.755 12.246 115.155 13.416C116.545 14.586 117.315 16.156 117.455 18.146H112.705C112.635 17.366 112.305 16.746 111.735 16.286C111.155 15.826 110.385 15.596 109.415 15.596C108.515 15.596 107.825 15.766 107.345 16.096C106.865 16.426 106.615 16.886 106.615 17.476C106.615 18.136 106.945 18.636 107.605 18.986C108.265 19.326 109.295 19.676 110.685 20.026C112.105 20.376 113.275 20.746 114.195 21.126C115.115 21.506 115.915 22.086 116.585 22.876C117.255 23.666 117.605 24.716 117.635 26.006C117.635 27.136 117.325 28.156 116.695 29.056C116.065 29.956 115.175 30.656 114.005 31.166C112.835 31.676 111.475 31.926 109.915 31.926C108.355 31.926 106.865 31.636 105.595 31.056L105.575 31.036Z"
              fill="white"
            />
            <path
              d="M127.455 16.0355V25.5255C127.455 26.1855 127.615 26.6655 127.935 26.9555C128.255 27.2555 128.795 27.3955 129.545 27.3955H131.845V31.5755H128.725C124.545 31.5755 122.455 29.5455 122.455 25.4855V16.0255H120.115V11.9555H122.455V7.10547H127.445V11.9555H131.835V16.0255H127.445L127.455 16.0355Z"
              fill="white"
            />
            <path
              d="M135.335 16.4245C136.125 14.8945 137.195 13.7145 138.555 12.8845C139.915 12.0545 141.425 11.6445 143.105 11.6445C144.565 11.6445 145.845 11.9445 146.945 12.5345C148.045 13.1245 148.925 13.8645 149.585 14.7645V11.9645H154.575V31.5845H149.585V28.7145C148.945 29.6345 148.065 30.3945 146.945 30.9945C145.825 31.5945 144.535 31.8945 143.065 31.8945C141.415 31.8945 139.905 31.4645 138.545 30.6145C137.185 29.7645 136.115 28.5645 135.325 27.0245C134.535 25.4745 134.135 23.7045 134.135 21.6945C134.135 19.6845 134.535 17.9545 135.325 16.4145L135.335 16.4245ZM148.885 18.6745C148.415 17.8145 147.775 17.1545 146.975 16.6945C146.175 16.2345 145.315 16.0045 144.385 16.0045C143.455 16.0045 142.615 16.2245 141.835 16.6745C141.055 17.1245 140.425 17.7745 139.945 18.6445C139.465 19.5045 139.215 20.5245 139.215 21.7045C139.215 22.8845 139.455 23.9145 139.945 24.8045C140.425 25.6945 141.065 26.3645 141.855 26.8445C142.645 27.3145 143.485 27.5545 144.385 27.5545C145.285 27.5545 146.165 27.3245 146.975 26.8645C147.775 26.4045 148.415 25.7445 148.885 24.8845C149.355 24.0245 149.595 22.9945 149.595 21.7845C149.595 20.5745 149.355 19.5445 148.885 18.6845V18.6745Z"
              fill="white"
            />
            <path
              d="M166.895 12.5658C167.925 11.9758 169.095 11.6758 170.415 11.6758V16.8858H169.105C167.545 16.8858 166.375 17.2558 165.585 17.9858C164.795 18.7158 164.395 19.9958 164.395 21.8058V31.5758H159.435V11.9558H164.395V15.0058C165.035 13.9658 165.865 13.1558 166.895 12.5658Z"
              fill="white"
            />
          </svg>
        </div>

        <div>
          <h2
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: 'white',
              margin: '0 0 24px 0',
              lineHeight: 1.5,
              animation: 'fadeInText 0.8s ease 1.5s both',
            }}
          >
            <span>Connect.</span>
            <span style={{ color: CYAN }}>*</span>
            <br />
            <span>Automate.</span>
            <span style={{ color: CYAN }}>*</span>
            <br />
            <span style={{ color: CYAN }}>Transform.</span>
            <span style={{ color: 'white' }}>*</span>
          </h2>

          <div
            style={{
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
              animation: 'fadeInText 0.8s ease 1.8s both',
            }}
          >
            {['AI', 'Data', 'Integrations', 'Compliance Ready'].map((label, i) => (
              <span
                key={i}
                style={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: 'rgba(255,255,255,0.7)',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 20,
                  padding: '5px 14px',
                  backdropFilter: 'blur(4px)',
                }}
              >
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '45%',
          background: `linear-gradient(to top, ${CHARCOAL}ee 0%, ${CHARCOAL}88 40%, transparent 100%)`,
          zIndex: 2,
        }}
      />
    </div>
  );
};

export default ULBrandBanner;
